import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  TextField,
  Skeleton,
  Stack,
} from '@mui/material';
import ShareButton from 'legacyfrontend/components/ui/ShareButton';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { DATE_KEYS, FALLBACK_IMAGE } from 'legacyfrontend/constants';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';
import useWhatsAppUrl from 'legacyfrontend/hooks/useWhatsAppUrl';
import AccordionCard from './AccordionCard';
import PreviewDescription from './PreviewDescription';
import { PreviewMailIcon, PreviewTabIcon, PreviewWhatsappIcon } from './PreviewIcons';
import HotelPreviewCard from './HotelPreviewCard';
import FlightGroup, { buildFlightGroups } from './FlightGroup';
import ServiceTiles from './ServiceTiles';
import 'react-quill/dist/quill.snow.css';
import RichTextHtml from 'legacyfrontend/components/RichTextHtml';
import { cleanListContent, isRichTextFilled, tariffCurrencyCode, tariffTaxAmount, tariffTotalAmount } from './previewUtils';
import { getDetailAccentStyle, getIconThemeTileStyle, resolvePackageIconThemeForViewer } from './iconTheme';
import './package-preview.css';

const PACKAGE_CONTENT_TABS = [
  { id: 'itinerario', type: 'itinerary', nameKey: 'packages.details.itinerary' },
  { id: 'incluye', type: 'html', nameKey: 'packages.preview.included', htmlKey: 'includedHtml' },
  { id: 'no-incluye', type: 'html', nameKey: 'packages.preview.notIncluded', htmlKey: 'notIncludedHtml' },
  { id: 'observaciones', type: 'html', nameKey: 'packages.preview.observations', htmlKey: 'observationsHtml' },
];

function getOrderedPackageContentTabs(ordenSecciones) {
  const defaultOrder = PACKAGE_CONTENT_TABS.map((tab) => tab.id);
  const order = Array.isArray(ordenSecciones) && ordenSecciones.length ? ordenSecciones : defaultOrder;
  const byId = PACKAGE_CONTENT_TABS.reduce((acc, tab) => {
    acc[tab.id] = tab;
    return acc;
  }, {});
  const ordered = order.map((id) => byId[id]).filter(Boolean);
  const missing = PACKAGE_CONTENT_TABS.filter((tab) => !ordered.some((item) => item.id === tab.id));
  return [...ordered, ...missing];
}

function formatSalidaOption(option) {
  if (!option?.dateSince) return '';
  const dateSince = formatWithMomentFormat(
    new Date(
      new Date(option.dateSince).getUTCFullYear(),
      new Date(option.dateSince).getUTCMonth(),
      new Date(option.dateSince).getUTCDate()
    ),
    DATE_KEYS.FORMAT.DAY_SHORT
  );
  const dateUntil = formatWithMomentFormat(
    new Date(
      new Date(option.dateUntil).getUTCFullYear(),
      new Date(option.dateUntil).getUTCMonth(),
      new Date(option.dateUntil).getUTCDate()
    ),
    DATE_KEYS.FORMAT.DAY_SHORT
  );
  return `Salida de ${dateSince} al ${dateUntil}`;
}

export default function PackagePreviewView({
  title,
  images,
  shareUrl,
  descriptionHtml,
  packageRefNumber,
  onCopy,
  destinations,
  originName,
  formattedDates,
  totalNights,
  includedItems,
  selectedPrice,
  currencyCode,
  currencyById,
  priceNote,
  availableSalidas,
  selectedSalidaId,
  onSelectSalida,
  loadingSalidas,
  tariffs,
  onSelectTariff,
  loadingPackageUpdate,
  itinerary,
  includedHtml,
  notIncludedHtml,
  observationsHtml,
  trips,
  hotels,
  onEmailClick,
  packageUrlWhatsapp,
  temaIconos,
  colorTemaIconos,
  ordenSecciones,
  isSupplierPackage,
  agencyBrandColor,
}) {
  const { t } = useTranslation('layout');
  const { whatsappUrl } = useWhatsAppUrl(packageUrlWhatsapp, title, packageRefNumber);
  const [imageIndex, setImageIndex] = useState(0);
  const selectedCurrencyCode = tariffCurrencyCode(selectedPrice, currencyCode, currencyById);
  const [openSection, setOpenSection] = useState(null);
  const iconTheme = resolvePackageIconThemeForViewer({
    temaIconos,
    colorTemaIconos,
    isSupplierPackage,
    agencyBrandColor,
  });
  const accentStyle = getDetailAccentStyle(iconTheme.mode, iconTheme.color);
  const iconThemeStyle = getIconThemeTileStyle(iconTheme.mode, iconTheme.color);

  const galleryImages = (images || []).map((img) => img?.imageUrl || img).filter(Boolean);
  const currentImage = galleryImages[imageIndex] || FALLBACK_IMAGE;
  const sortedItinerary = [...(itinerary || [])]
    .filter((item) => (item.title || '').trim() || isRichTextFilled(item.description))
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const flightGroups = buildFlightGroups(trips, t);
  const htmlByKey = { includedHtml, notIncludedHtml, observationsHtml };
  const contentTabs = getOrderedPackageContentTabs(ordenSecciones).filter((tab) =>
    tab.type === 'itinerary' ? sortedItinerary.length > 0 : isRichTextFilled(htmlByKey[tab.htmlKey]),
  );

  function toggleSection(id) {
    setOpenSection((current) => (current === id ? null : id));
  }

  function copy(text) {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    if (onCopy) onCopy(text);
  }

  return (
    <div className='package-detail-container package-preview p-4' style={accentStyle || undefined}>
      <div className='preview-split'>
        <div className='preview-gallery-col'>
          <div className='preview-card preview-gallery'>
            <div className='preview-title-row'>
              <h1>{title}</h1>
              <ShareButton url={shareUrl} title={t('packages.preview.shareTitle')} variant='package' />
            </div>
            <div className='carousel-stage'>
              <div className='carousel-media'>
                {galleryImages.length > 1 && (
                  <button
                    className='carousel-arrow prev'
                    type='button'
                    aria-label={t('packages.preview.prevImage')}
                    onClick={() =>
                      setImageIndex((index) => (index === 0 ? galleryImages.length - 1 : index - 1))
                    }
                  >
                    ‹
                  </button>
                )}
                {galleryImages.length ? (
                  <img src={currentImage} alt='' />
                ) : (
                  <div className='carousel-empty'>{t('packages.preview.noImages')}</div>
                )}
                {galleryImages.length > 1 && (
                  <button
                    className='carousel-arrow next'
                    type='button'
                    aria-label={t('packages.preview.nextImage')}
                    onClick={() =>
                      setImageIndex((index) =>
                        index === galleryImages.length - 1 ? 0 : index + 1
                      )
                    }
                  >
                    ›
                  </button>
                )}
              </div>
            </div>
            {galleryImages.length > 1 ? (
              <div className='carousel-thumbs'>
                {galleryImages.map((src, index) => (
                  <button
                    key={src}
                    className={index === imageIndex ? 'carousel-thumb active' : 'carousel-thumb'}
                    type='button'
                    onClick={() => setImageIndex(index)}
                  >
                    <img src={src} alt='' />
                  </button>
                ))}
              </div>
            ) : null}
            {isRichTextFilled(descriptionHtml) ? (
              <PreviewDescription html={cleanListContent(descriptionHtml)} />
            ) : null}
          </div>

          <div className='pkg-cards-row'>
            <div className='preview-card'>
              <h2>{t('packages.details.package_detail')}</h2>
              <hr className='detail-divider' />
              {packageRefNumber ? (
                <div className='detail-card-head'>
                  <span className='detail-ref'>
                    {t('packages.preview.refNumber', { ref: packageRefNumber })}
                  </span>
                  <button
                    className='copy-ref-btn'
                    type='button'
                    aria-label={t('packages.preview.copy')}
                    onClick={() => copy(packageRefNumber)}
                  >
                    ⧉
                  </button>
                </div>
              ) : null}
              <div className='detail-section-label' style={{ marginTop: 20 }}>
                {t('packages.preview.destinations')}
              </div>
              <div className='destino-chip-list'>
                {destinations?.length ? (
                  destinations.map((destino) => {
                    const city = destino?.city?.name || t('packages.preview.cityFallback');
                    const country = destino?.city?.country?.name;
                    return (
                      <span className='destino-chip' key={destino?.id || city}>
                        <strong>{city}</strong>
                        {country ? ` · ${country}` : ''}
                      </span>
                    );
                  })
                ) : (
                  <span className='detail-ref'>{t('packages.preview.noDestinations')}</span>
                )}
              </div>
              <hr className='detail-divider' />
              <div className='detail-stat-row'>
                <div className='detail-stat'>
                  <span className='detail-stat-label'>{t('packages.preview.from')}</span>
                  <span className='detail-stat-value'>{originName || '—'}</span>
                </div>
                <div className='detail-stat'>
                  <span className='detail-stat-label'>{t('packages.details.departure')}</span>
                  <span className='detail-stat-value'>{formattedDates}</span>
                </div>
                <div className='detail-stat'>
                  <span className='detail-stat-label'>{t('packages.preview.nights')}</span>
                  <span className='detail-stat-value'>
                    {totalNights ?? '—'}
                  </span>
                </div>
              </div>
              {includedItems?.length > 0 ? (
                <>
                  <hr className='detail-divider' />
                  <div className='detail-section-label'>{t('packages.details.services')}</div>
                  <ServiceTiles items={includedItems} />
                </>
              ) : null}
            </div>

            <div className='preview-card'>
              <h2>{t('packages.preview.priceTitle')}</h2>
              <hr className='detail-divider' />
              <div className='price-total-row'>
                <span>{t('packages.preview.priceTotal')}</span>
                <strong>
                  {selectedPrice
                    ? getFinalPrice({
                        currencyCode: selectedCurrencyCode,
                        total: tariffTotalAmount(selectedPrice),
                      })
                    : `${currencyCode} 0`}
                </strong>
              </div>
              <hr className='detail-divider' />
              <div className='price-sub-row'>
                <span>{t('packages.preview.productPrice')}</span>
                <span>
                  {selectedPrice
                    ? getFinalPrice({
                        currencyCode: selectedCurrencyCode,
                        total: selectedPrice.amount,
                      })
                    : `${currencyCode} 0`}
                </span>
              </div>
              <div className='price-sub-row'>
                <span>{t('packages.preview.taxes')}</span>
                <span>
                  {selectedPrice
                    ? getFinalPrice({
                        currencyCode: selectedCurrencyCode,
                        total: tariffTaxAmount(selectedPrice),
                      })
                    : `${currencyCode} 0`}
                </span>
              </div>
              {priceNote ? (
                <>
                  <hr className='detail-divider' />
                  <div className='price-note'>
                    <strong>{t('packages.preview.note')}</strong>
                    {priceNote}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {availableSalidas?.length > 0 ? (
            <div className='preview-card preview-salidas-card'>
              <Autocomplete
                id='salidas-autocomplete'
                options={availableSalidas}
                loading={loadingSalidas}
                filterOptions={(options) => options}
                getOptionLabel={formatSalidaOption}
                value={availableSalidas.find((s) => s.salidaId === selectedSalidaId) || null}
                onChange={(event, newValue) => {
                  if (newValue) onSelectSalida(newValue.salidaId);
                }}
                isOptionEqualToValue={(option, value) => option.salidaId === value.salidaId}
                renderInput={(params) => (
                  <TextField {...params} label={t('packages.details.departure')} variant='outlined' />
                )}
              />
            </div>
          ) : null}

          <section className='preview-card'>
            <h2>{t('packages.details.rates')}</h2>
            {loadingPackageUpdate ? (
              <Stack spacing={1} sx={{ marginTop: '1rem' }}>
                <Skeleton variant='rectangular' height={50} />
                <Skeleton variant='rectangular' height={60} />
                <Skeleton variant='rectangular' height={60} />
              </Stack>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: 16 }}>
                <table className='tramos-table'>
                  <thead>
                    <tr>
                      <th />
                      <th>{t('packages.preview.availableDepartures')}</th>
                      <th>{t('packages.preview.rate')}</th>
                      <th>{t('packages.preview.taxes')}</th>
                      <th>{t('packages.preview.totalPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tariffs || []).map((precio) => {
                      const selected = selectedPrice?.id === precio?.id;
                      const rowCurrencyCode = tariffCurrencyCode(precio, currencyCode, currencyById);
                      return (
                        <tr
                          key={precio?.id}
                          className={selected ? 'tarifa-row-selected' : undefined}
                          onClick={() => onSelectTariff(precio)}
                        >
                          <td>
                            <input
                              className='tarifa-radio'
                              type='radio'
                              checked={selected}
                              onChange={() => onSelectTariff(precio)}
                            />
                          </td>
                          <td>{formattedDates}</td>
                          <td>
                            {typeof precio?.baseType?.description === 'string'
                              ? precio.baseType.description
                              : t('packages.preview.rateFallback')}
                          </td>
                          <td>
                            {getFinalPrice({
                              currencyCode: rowCurrencyCode,
                              total: tariffTaxAmount(precio),
                            })}
                          </td>
                          <td className='tarifa-total'>
                            {getFinalPrice({
                              currencyCode: rowCurrencyCode,
                              total: tariffTotalAmount(precio),
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className='preview-data-column'>
          <div className='preview-accordion'>
            {contentTabs.map((tab) => (
              <AccordionCard
                key={tab.id}
                id={tab.id}
                title={t(tab.nameKey)}
                open={openSection === tab.id}
                onToggle={() => toggleSection(tab.id)}
                iconStyle={iconThemeStyle}
              >
                {tab.type === 'itinerary' ? (
                  <div className='timeline'>
                    {sortedItinerary.map((item, index) => (
                      <div className='timeline-item' key={item.id || index}>
                        <div className='timeline-number'>{item.order || index + 1}</div>
                        <div>
                          <div className='timeline-title'>{item.title}</div>
                          <RichTextHtml
                            className='timeline-description'
                            html={item.description || ''}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <RichTextHtml html={cleanListContent(htmlByKey[tab.htmlKey])} />
                )}
              </AccordionCard>
            ))}

            {trips?.length > 0 ? (
              <AccordionCard
                id='transporte'
                title={t('packages.preview.transport')}
                open={openSection === 'transporte'}
                onToggle={() => toggleSection('transporte')}
                iconStyle={iconThemeStyle}
              >
                {loadingPackageUpdate ? (
                  <Stack spacing={2}>
                    <Skeleton variant='rectangular' height={120} sx={{ borderRadius: '10px' }} />
                    <Skeleton variant='rectangular' height={120} sx={{ borderRadius: '10px' }} />
                  </Stack>
                ) : (
                  flightGroups.map((group) => (
                    <FlightGroup
                      key={group.key}
                      title={group.title}
                      trips={group.trips}
                      kind={group.kind}
                    />
                  ))
                )}
              </AccordionCard>
            ) : null}

            {hotels?.length > 0 ? (
              <AccordionCard
                id='hoteles'
                title={t('packages.details.hotels')}
                open={openSection === 'hoteles'}
                onToggle={() => toggleSection('hoteles')}
                iconStyle={iconThemeStyle}
              >
                {loadingPackageUpdate ? (
                  <Stack spacing={2}>
                    <Skeleton variant='rectangular' height={80} sx={{ borderRadius: '10px' }} />
                    <Skeleton variant='rectangular' height={80} sx={{ borderRadius: '10px' }} />
                  </Stack>
                ) : (
                  hotels.map((hotel) => (
                    <HotelPreviewCard key={hotel.id || hotel.clientId} hotel={hotel} />
                  ))
                )}
              </AccordionCard>
            ) : null}

            <div className='preview-accordion-card preview-contact-fixed'>
              <div className='preview-accordion-trigger'>
                <span className='preview-card-icon icon-contacto' style={iconThemeStyle || undefined}>
                  <PreviewTabIcon tabId='contacto' />
                </span>
                <span className='preview-card-copy'>
                  <strong>{t('packages.preview.contact')}</strong>
                </span>
              </div>
              <div className='preview-accordion-body'>
                <div className='preview-contact-actions'>
                  <button className='preview-contact-action mail' type='button' onClick={onEmailClick}>
                    <PreviewMailIcon />
                    {t('packages.preview.mail')}
                  </button>
                  <a
                    className={[
                      'preview-contact-action whatsapp',
                      whatsappUrl ? '' : 'is-disabled',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    href={whatsappUrl || undefined}
                    onClick={(event) => {
                      if (!whatsappUrl) event.preventDefault();
                    }}
                    rel={whatsappUrl ? 'noopener noreferrer' : undefined}
                    target={whatsappUrl ? '_blank' : undefined}
                    aria-disabled={!whatsappUrl}
                  >
                    <PreviewWhatsappIcon />
                    {t('packages.preview.whatsapp')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
